///
/// EarthBrowser
///
/// Created by Matt Giger on 10/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Single top-center slot for transient, non-modal notices (conversion
/// banner, shared-content banner, save-view toast, offers). Each notice
/// claims the slot with a priority while it wants to show; only the
/// highest-priority claimant renders, so notices never stack or collide
/// with each other or the corner chrome. Modal surfaces (interstitial,
/// sign-in wall, checkout) are not slot members.
///

import { cn } from "@/utils/utils"
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"

/** Higher wins. Contextual, user-triggered notices outrank marketing. */
export const slotPriority = {
	/** Recipient just opened a share link */
	sharedContent: 50,
	/** The user just saved a view: inline rename */
	saveViewToast: 40,
	/** Second anonymous save: sync pitch at the moment it's relevant */
	savedViewPrompt: 30,
	/** Tenure-based full-access offer for signed-in users */
	fullAccessOffer: 20,
	/** Rung-1 passive conversion banner */
	conversionBanner: 10,
} as const

interface SlotContextValue {
	claim: (id: string, priority: number) => void
	release: (id: string) => void
	activeId: string | null
}

const SlotContext = createContext<SlotContextValue | null>(null)

/**
 * Positioned container plus the claim registry. Phones put it on a second
 * row under the Layers button and the top-right cluster; from md up there
 * is room on the top row between them.
 */
export function NotificationSlot({ children, className }: { children: ReactNode; className?: string }) {
	const [claims, setClaims] = useState<Record<string, number>>({})

	const claim = useCallback((id: string, priority: number) => {
		setClaims(prev => prev[id] === priority ? prev : { ...prev, [id]: priority })
	}, [])
	const release = useCallback((id: string) => {
		setClaims(prev => {
			if (!(id in prev)) return prev
			const next = { ...prev }
			delete next[id]
			return next
		})
	}, [])

	const activeId = useMemo(() => {
		let best: string | null = null
		let bestPriority = -Infinity
		for (const [id, priority] of Object.entries(claims)) {
			if (priority > bestPriority) {
				best = id
				bestPriority = priority
			}
		}
		return best
	}, [claims])

	const value = useMemo(() => ({ claim, release, activeId }), [claim, release, activeId])

	return (
		<SlotContext.Provider value={value}>
			<div
				className={cn(
					"absolute left-1/2 -translate-x-1/2 z-30 flex justify-center",
					// Phones: below the top control row; desktop: level with it.
					// Both clear the status bar inset in the native apps
					"top-[calc(3.5rem+var(--safe-area-inset-top))] md:top-[calc(0.5rem+var(--safe-area-inset-top))]",
					"max-w-[calc(100vw-1rem)] md:max-w-[calc(100vw-22rem)]",
					activeId === null && "pointer-events-none",
					className
				)}
				data-notification-slot
			>
				{children}
			</div>
		</SlotContext.Provider>
	)
}

/**
 * Claim the slot while `wants` is true. Returns whether this notice is the
 * one that should render right now. Notices keep their own visibility
 * logic and analytics; this only arbitrates the shared space.
 */
export function useNotificationSlot(id: string, wants: boolean, priority: number): boolean {
	const ctx = useContext(SlotContext)
	if (!ctx) {
		throw new Error("useNotificationSlot must be used inside <NotificationSlot>")
	}
	const { claim, release, activeId } = ctx
	useEffect(() => {
		if (wants) {
			claim(id, priority)
			return () => release(id)
		}
		release(id)
	}, [id, wants, priority, claim, release])
	return wants && activeId === id
}

/** Shared glass shell for single-line notices in the slot */
export const slotPillClass = cn(
	"flex items-center gap-2 h-9 pl-3 pr-1",
	"bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl",
	"animate-in fade-in slide-in-from-top-2 duration-300"
)

/** Small action button inside a slot pill */
export const slotActionClass = "h-7 rounded-lg px-3 text-xs"

/** Dismiss "x" inside a slot pill */
export const slotDismissClass = "p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
